import { AlertTriangle } from 'lucide-react-native';
import { StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  formatAgentProductLine,
  isGenericProductName,
  suggestNameFromDescription,
} from '../utils/productNaming';

interface ProductNameGuidanceProps {
  testIDPrefix: string;
  name: string;
  description?: string | null;
  priceRange?: string | null;
  aliases?: readonly string[] | null;
  onUseSuggestion: (suggestion: string) => void;
  showPreview?: boolean;
  variant?: 'editing' | 'saved';
}

export function ProductNameGuidance({
  testIDPrefix,
  name,
  description,
  priceRange,
  aliases,
  onUseSuggestion,
  showPreview = true,
  variant = 'editing',
}: Readonly<ProductNameGuidanceProps>) {
  const { t } = useTranslation();

  const isGeneric = isGenericProductName(name);
  const suggestion = suggestNameFromDescription(name, description);
  const previewLine = showPreview
    ? formatAgentProductLine({ name, description, priceRange, aliases })
    : null;

  if (!isGeneric && !previewLine) return null;

  return (
    <VStack spacing="xs" testID={testIDPrefix}>
      {isGeneric ? (
        <Box
          testID={`${testIDPrefix}-generic-warning`}
          style={styles.warning}
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          <HStack spacing="xs" align="center">
            <AlertTriangle size={14} color={colors.status.warning.main} />
            <Text variant="caption" color={colors.text.primary} style={styles.warningText}>
              {t(
                variant === 'saved'
                  ? 'onboardingWizard.products.naming.savedGenericNotice'
                  : 'onboardingWizard.products.naming.genericWarning',
              )}
            </Text>
          </HStack>
          {suggestion ? (
            <VStack spacing="xs" testID={`${testIDPrefix}-suggestion`}>
              <Text variant="caption" color={colors.text.primary}>
                {t('onboardingWizard.products.naming.suggestion', { suggestion })}
              </Text>
              <Box style={styles.action}>
                <Button
                  testID={`${testIDPrefix}-use-suggestion`}
                  variant="outline"
                  size="sm"
                  onPress={() => onUseSuggestion(suggestion)}
                  accessibilityLabel={t('onboardingWizard.products.naming.useAsNameLabel', {
                    suggestion,
                  })}
                >
                  {t('onboardingWizard.products.naming.useAsName')}
                </Button>
              </Box>
            </VStack>
          ) : null}
        </Box>
      ) : null}

      {previewLine ? (
        <VStack spacing="xxs" testID={`${testIDPrefix}-preview`}>
          <Text variant="caption" color={colors.text.secondary} style={styles.previewTitle}>
            {t('onboardingWizard.products.naming.previewTitle')}
          </Text>
          <Text variant="caption" color={colors.text.primary}>
            {previewLine}
          </Text>
        </VStack>
      ) : null}
    </VStack>
  );
}

const styles = StyleSheet.create({
  warning: {
    borderWidth: 1,
    borderColor: colors.status.warning.border,
    backgroundColor: colors.status.warning.bg,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.sm,
    gap: tokens.spacing.xs,
  },
  warningText: {
    flex: 1,
  },
  action: {
    alignSelf: 'flex-start',
  },
  previewTitle: {
    fontWeight: '600',
  },
});
